import { jsx as _jsx } from "react/jsx-runtime";
import { GoogleAuthProvider, getRedirectResult, linkWithCredential, linkWithPopup, linkWithRedirect, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, } from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, } from "react";
import { auth } from "./firebase";
export const AuthContent = createContext({
    user: null,
    loading: true,
});
export function hasAdminAccess(claims) {
    return claims.systemAdmin === true || claims.systemAdmin === "true";
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            setLoading(false);
        });
        return unsubscribe;
    }, []);
    const value = useMemo(() => ({ user, loading }), [user, loading]);
    return _jsx(AuthContent.Provider, { value: value, children: children });
}
export async function loginWithEmailAndPassword(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const tokenResult = await userCredential.user.getIdTokenResult(true);
    if (!hasAdminAccess(tokenResult.claims)) {
        await auth.signOut();
        throw new Error("Access denied: systemAdmin privileges required.");
    }
    return userCredential;
}
export function logout() {
    return auth.signOut();
}
export function useAuth() {
    const context = useContext(AuthContent);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
export function isGoogleLinked(user) {
    if (!user)
        return false;
    return user.providerData.some((provider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID);
}
export function sendPasswordReset(email) {
    return sendPasswordResetEmail(auth, email);
}
export async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    try {
        return await signInWithPopup(auth, provider);
    }
    catch (error) {
        if (error?.code === "auth/popup-blocked" ||
            error?.code ===
                "auth/cancelled-popup-request") {
            await signInWithRedirect(auth, provider);
            return null;
        }
        throw error;
    }
}
export async function linkGoogleAccount(user) {
    const provider = new GoogleAuthProvider();
    try {
        return await linkWithPopup(user, provider);
    }
    catch (error) {
        if (error?.code === "auth/popup-blocked" ||
            error?.code ===
                "auth/cancelled-popup-request") {
            await linkWithRedirect(user, provider);
            return null;
        }
        throw error;
    }
}
export async function linkGoogleWithPassword(email, password, pendingCredential) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return await linkWithCredential(userCredential.user, pendingCredential);
}
export { GoogleAuthProvider, getRedirectResult };
